import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Icon, Loader, Placeholder } from 'react-frontier';
import { firstBy } from 'thenby';
import classNames from 'classnames';
import { useUser } from '../Hooks';
import UserAccess from '../UserAccess';
import { PromoterComment, UserItem } from '../SiteClasses';
import { randomRange, formatUnix, nowUnix } from '../SiteUtil';
import Toast from '../Toast';
import API from '../API';

const MENTION_REGEX = /@([^\s@]+)/g;
const MAX_LENGTH = 1024;

interface CommentsProps{
	prospect_id?: number,
	promoter_id?: number,
	include_prospect_id?: number,
}

var Comments = (props: CommentsProps)=>{
	var { user, hasAccess } = useUser();
	var canEdit = hasAccess(props.prospect_id ? UserAccess.PROSPECTS.EDIT_PROSPECTS : UserAccess.PROMOTERS.EDIT_PROMOTERS);
	var target = props.prospect_id ? { prospect_id: props.prospect_id } : { promoter_id: props.promoter_id };
	var targetKey = props.prospect_id || props.promoter_id;

	var [comments, setComments] = useState<PromoterComment[]>(null);
	var [value, setValue] = useState<string>('');
	var [mentionOpen, setMentionOpen] = useState<boolean>(false);
	var [mentionQuery, setMentionQuery] = useState<string>('');
	var [mentionUsers, setMentionUsers] = useState<UserItem[]>([]);
	var [mentionIndex, setMentionIndex] = useState<number>(0);
	var [mentionMap, setMentionMap] = useState<Map<string, number>>(new Map());

	var inputRef = useRef<HTMLTextAreaElement>(null);
	var commentsRef = useRef<PromoterComment[]>(null);
	var searchTimeout = useRef<any>(null);
	var caretToSet = useRef<number>(null);
	commentsRef.current = comments;

	useEffect(()=>{ get(); }, [targetKey, props.include_prospect_id]);

	useEffect(()=>{
		if(!mentionOpen || mentionQuery.length===0){ setMentionUsers([]); return; }
		if(searchTimeout.current) clearTimeout(searchTimeout.current);
		searchTimeout.current = setTimeout(()=>{
			API.searchUsers(mentionQuery).then(res=>{ if(!res.error) setMentionUsers(res.data); }).catch(()=>{});
		}, 350);
	}, [mentionQuery, mentionOpen]);

	useEffect(()=>{
		if(caretToSet.current!=null && inputRef.current){
			inputRef.current.focus();
			inputRef.current.setSelectionRange(caretToSet.current, caretToSet.current);
			caretToSet.current = null;
		}
		autoGrow();
	}, [value]);

	var autoGrow = ()=>{
		var el = inputRef.current;
		if(!el) return;
		el.style.height = 'auto';
		el.style.height = Math.min(el.scrollHeight, 140) + 'px';
	};

	var get = ()=>{
		setComments(null);
		API.getComments({ ...target, include_prospect_id: props.include_prospect_id }).then(res=>{
			if(res.error){ Toast.error(res.message); return setComments([]); }
			setComments(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error cargando los comentarios. (LCL-1)');
			setComments([]);
		});
	};

	var detectMention = (text: string, caret: number)=>{
		var before = text.substring(0, caret);
		var m = before.match(/@([^\s@]*)$/);
		if(m){
			setMentionOpen(true);
			setMentionQuery(m[1]);
			setMentionIndex(0);
		}else{
			setMentionOpen(false);
			setMentionQuery('');
		}
	};

	var onChange = (e: React.ChangeEvent<HTMLTextAreaElement>)=>{
		setValue(e.target.value);
		detectMention(e.target.value, e.target.selectionStart);
	};

	var insertMention = (u: UserItem)=>{
		var el = inputRef.current;
		var caret = el ? el.selectionStart : value.length;
		var before = value.substring(0, caret);
		var after = value.substring(caret);
		var m = before.match(/@([^\s@]*)$/);
		if(!m) return;
		var start = caret - m[0].length;
		var username = (u.first_name + u.last_name).replace(/\s+/g, '');
		var insert = `@${username} `;
		setMentionMap(prev=> new Map(prev).set(username, u.user_id));
		caretToSet.current = start + insert.length;
		setValue(value.substring(0, start) + insert + after);
		setMentionOpen(false);
		setMentionQuery('');
	};

	var onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>)=>{
		if(mentionOpen && mentionUsers.length>0){
			if(e.key==='ArrowDown'){ e.preventDefault(); return setMentionIndex(i=>Math.min(i+1, mentionUsers.length-1)); }
			if(e.key==='ArrowUp'){ e.preventDefault(); return setMentionIndex(i=>Math.max(i-1, 0)); }
			if(e.key==='Enter' || e.key==='Tab'){ e.preventDefault(); return insertMention(mentionUsers[mentionIndex]); }
			if(e.key==='Escape'){ return setMentionOpen(false); }
		}
		if(e.key==='Enter' && !e.shiftKey){
			e.preventDefault();
			send();
		}
	};

	var extractMentions = (content: string)=>{
		var ids = new Set<number>();
		var match: RegExpExecArray, re = new RegExp(MENTION_REGEX);
		while((match = re.exec(content))!==null){
			var id = mentionMap.get(match[1]);
			if(id) ids.add(id);
		}
		return Array.from(ids);
	};

	var send = ()=>{
		var content = value.trim();
		if(content.length===0) return;
		if(content.length>MAX_LENGTH){ return Toast.error(`El comentario es demasiado largo (máx. ${MAX_LENGTH}).`); }

		var mentioned = extractMentions(content);
		var temp: PromoterComment = {
			comment_id: null,
			...target,
			user_id: user.admin_id,
			user_creator_name: `${user.first_name} ${user.last_name}`,
			content,
			date_created: nowUnix(),
			local_id: randomRange(1, 999999999),
			sending: true,
		};
		setComments([...(commentsRef.current || []), temp].sort(firstBy('date_created', 'desc')));
		setValue('');
		setMentionMap(new Map());
		setMentionOpen(false);

		API.createComment(target, content, mentioned).then(res=>{
			var rest = (commentsRef.current || []).filter(a=>a.local_id!==temp.local_id);
			if(res.error){ setComments(rest); return Toast.error(res.message); }
			setComments([...rest, { ...temp, sending: false, local_id: null, comment_id: res.data.comment_id }].sort(firstBy('date_created', 'desc')));
		}).catch(()=>{
			setComments((commentsRef.current || []).filter(a=>a.local_id!==temp.local_id));
			Toast.error('Hubo un error agregando el comentario. (LCL-1)');
		});
	};

	var remove = (c: PromoterComment)=>{
		API.deleteComment(c.comment_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setComments((commentsRef.current || []).filter(a=>a.comment_id!==c.comment_id));
		}).catch(()=>{
			Toast.error('Hubo un error eliminando el comentario. (LCL-1)');
		});
	};

	var renderContent = (content: string)=>{
		var parts: React.ReactNode[] = [];
		var last = 0, match: RegExpExecArray, re = new RegExp(MENTION_REGEX), key = 0;
		while((match = re.exec(content))!==null){
			if(match.index>last) parts.push(<span key={key++}>{content.substring(last, match.index)}</span>);
			parts.push(<span key={key++} className='mention'>{match[0]}</span>);
			last = match.index + match[0].length;
		}
		if(last<content.length) parts.push(<span key={key++}>{content.substring(last)}</span>);
		return parts;
	};

	return <div className='record-section'>
		<div className='section-header'>Comentarios</div>
		<div className='section-body'>
		<div className={classNames('comments-container', { empty: comments && comments.length===0 })}>
		{canEdit && <div className='textbox'>
			<textarea
				ref={inputRef}
				className='input'
				rows={1}
				placeholder='Ingresa tu comentario'
				value={value}
				onChange={onChange}
				onKeyDown={onKeyDown}
			/>
			{mentionOpen && mentionUsers.length>0 && (
				<div className='mentions-dropdown'>
					{mentionUsers.map((u, i)=>(
						<div
							key={u.user_id}
							className={classNames('mention-item', { selected: i===mentionIndex })}
							onMouseDown={(e)=>{ e.preventDefault(); insertMention(u); }}
							onMouseEnter={()=>setMentionIndex(i)}
						>
							<Icon name='user' />
							<span className='name'>{u.name}</span>
							<span className='email'>{u.email}</span>
						</div>
					))}
				</div>
			)}
			<div className='right'>
				<Button iconName='paper-plane' color='blue' icon size='small' onClick={send} />
			</div>
		</div>}
		{!comments ? (
			<div className='comments'>
				{[0,1,2,3].map(i=>(
					<div key={i} className={classNames('comment placeholder', { sent: i%2===1 })}>
						<div className='content'><div className='message'><div className='text'><Placeholder /></div></div></div>
					</div>
				))}
			</div>
		) : comments.length===0 ? (
			<div className='empty'>
				<Header text='Sin comentarios' subtext='Esta sección se encuentra vacía' iconName='comments' />
			</div>
		) : (
			<div className='comments'>
				{comments.map(c=>{
					var mine = c.user_id===user?.admin_id;
					return <div key={`cm-${c.comment_id || c.local_id}`} className={classNames('comment', { sent: mine, sending: c.sending })}>
						{!mine && (
							<div className='sender'>
								{c.user_creator_name}
								<div className='date'>{formatUnix(c.date_created, 'dd/MMM HH:mm')}</div>
							</div>
						)}
						<div className='content'>
							<div className='message'>
								{c.sending ? (
									<Loader inline size={14} />
								) : mine && canEdit && (
									<span className='delete' onClick={()=>remove(c)}><Icon name='trash' /></span>
								)}
								<div className='text'>{renderContent(c.content)}</div>
							</div>
							{mine && <div className='date'>{formatUnix(c.date_created, 'dd/MMM HH:mm')}</div>}
						</div>
					</div>
				})}
			</div>
		)}
	</div>
		</div>
		</div>;
};

export default Comments;
